import {
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Popover
} from "@material-ui/core";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import StoreIcon from "@material-ui/icons/Store";
import { useTemplateStyles } from "@saleor/dropOff/styles/templates";
import { GetAvailableTemplates_templates_edges_node } from "@saleor/dropOff/types/GetAvailableTemplates";
import { maybe } from "@saleor/misc";
import * as React from "react";

import {
  DropOffHomeLocIndicator,
  DropOffHomeLocOutletIndicatorEnum
} from "../DropOffHomeLocLabels/DropOffHomeLocLabels";
import { DropOffTemplateSwitch } from "./DropOffTemplateSwitch";

interface DropOffTemplateListCardProps {
  disableAction: boolean;
  templateDetails: GetAvailableTemplates_templates_edges_node;
  filterOutletStatus: number;
  onDuplicate: (val) => void;
  onEdit: (val) => void;
  onDelete: (val) => void;
  onRename: (val, name) => void;
  onView: (templateId, mallId) => void;
  onSetActiveTemplate: (val) => void;
}

const DropOffTemplateListCard: React.FC<DropOffTemplateListCardProps> = props => {
  const {
    disableAction,
    templateDetails,
    filterOutletStatus,
    onDuplicate,
    onEdit,
    onDelete,
    onRename,
    onView,
    onSetActiveTemplate
  } = props;

  const classes = useTemplateStyles({});

  const menuItems: string[] = ["Duplicate", "Edit", "Delete", "Rename"];
  const draftItems: string[] = ["Continue Setup", "Delete"];
  const activeItems: string[] = [
    "View",
    "Duplicate",
    "Edit",
    "Delete",
    "Rename"
  ];
  const viewItems: string[] = ["View"];
  const isDraft = templateDetails.status === "DRAFT";

  const [anchorModal, setAnchorModal] = React.useState(null);
  const [activeTemplate, setActiveTemplate] = React.useState(false);
  const [homeLocOutletIndicator, setHomeLocOutletIndicator] = React.useState(
    DropOffHomeLocOutletIndicatorEnum.UNREGISTERED
  );
  const [ctrRegistered, setCtrRegistered] = React.useState("");
  const [ctrUnregistered, setCtrUnregistered] = React.useState("");

  const openModal = Boolean(anchorModal);

  React.useEffect(() => {
    if (templateDetails.status === "ACTIVE") {
      setActiveTemplate(true);
    } else {
      setActiveTemplate(false);
    }

    const homeLocsWithOutletId = templateDetails?.dropOffLocations?.edges?.filter(
      edge => edge?.node?.outletId
    );
    if (
      templateDetails?.dropOffLocations?.totalCount ===
      homeLocsWithOutletId.length
    ) {
      setHomeLocOutletIndicator(DropOffHomeLocOutletIndicatorEnum.REGISTERED);
      setCtrRegistered("(" + homeLocsWithOutletId.length.toString() + ")");
      setCtrUnregistered("");
    } else if (homeLocsWithOutletId.length > 0) {
      setHomeLocOutletIndicator(DropOffHomeLocOutletIndicatorEnum.INCOMPLETE);
      setCtrRegistered("(" + homeLocsWithOutletId.length.toString() + ")");
      setCtrUnregistered(
        "(" +
          (
            templateDetails?.dropOffLocations?.totalCount -
            homeLocsWithOutletId.length
          ).toString() +
          ")"
      );
    } else {
      setHomeLocOutletIndicator(DropOffHomeLocOutletIndicatorEnum.UNREGISTERED);
      setCtrRegistered("");
      setCtrUnregistered(
        "(" + templateDetails?.dropOffLocations?.totalCount.toString() + ")"
      );
    }
  }, [templateDetails, filterOutletStatus]);

  const handleClose = () => {
    setAnchorModal(null);
  };

  const switchChange = () => {
    onSetActiveTemplate(templateDetails.id);
  };

  const getStatusContent = () => {
    let contentToRender = null;
    if (isDraft) {
      contentToRender = (
        <p>Complete the set up of this template to set as active</p>
      );
    } else if (!activeTemplate) {
      contentToRender = (
        <FormControlLabel
          value="start"
          control={
            <DropOffTemplateSwitch
              disabled={disableAction}
              onChange={switchChange}
            />
          }
          label="Set as Active"
          checked={activeTemplate}
          labelPlacement="start"
        />
      );
    } else {
      contentToRender = (
        <div className={classes.switchAlreadyActive}>
          <h3>Active</h3>
          <CheckCircleIcon className={classes.switchCircleActive} />
        </div>
      );
    }
    return contentToRender;
  };

  const getMenuItems = () => {
    let itemsToMap;

    if (activeTemplate) {
      itemsToMap = disableAction ? viewItems : activeItems;
    } else if (isDraft) {
      itemsToMap = draftItems;
    } else {
      itemsToMap = menuItems;
    }

    return itemsToMap;
  };

  return (
    <div className={classes.templateItemRow}>
      <div
        className={`${classes.templateItemCard} ${
          activeTemplate ? classes.templateItemCardActive : ""
        }`}
      >
        <div className={classes.itemCardDetails}>
          <h3>{templateDetails.name}</h3>
          <div className={classes.templateItemStatusListIcons}>
            <div className={classes.itemIconStatus}>
              <DropOffHomeLocIndicator
                status={homeLocOutletIndicator}
                ctrRegistered={ctrRegistered}
                ctrUnregistered={ctrUnregistered}
              />

              <span className={classes.itemIconCount}>
                {maybe(() => templateDetails.dropOffLocations.totalCount)}
              </span>
              <span className={classes.itemIconLabel}>Home Location</span>
            </div>
            <div className={classes.itemIconStatus}>
              <AssignmentTurnedInIcon className={classes.itemIconSvg} />
              <span className={classes.itemIconCount}>
                {maybe(() => templateDetails.taskAreas.totalCount)}
              </span>
              <span className={classes.itemIconLabel}>Task Areas</span>
            </div>
            <div className={classes.itemIconStatus}>
              <StoreIcon className={classes.itemIconSvg} />
              <span className={classes.itemIconCount}>
                {maybe(() => templateDetails.assignedStores.totalCount)}
              </span>
              <span className={classes.itemIconLabel}>Stores</span>
            </div>
          </div>
        </div>
        <div className={classes.itemCardStatus}>{getStatusContent()}</div>
      </div>
      <div className={classes.templateItemAction}>
        <div>
          <IconButton
            className={classes.buttonAction}
            disabled={!activeTemplate && disableAction}
            onClick={e => setAnchorModal(e.currentTarget)}
          >
            <MoreVertIcon />
          </IconButton>
          <Popover
            open={openModal}
            anchorEl={anchorModal}
            onClose={() => setAnchorModal(null)}
            anchorOrigin={{
              horizontal: "center",
              vertical: "bottom"
            }}
            transformOrigin={{
              horizontal: "center",
              vertical: "top"
            }}
          >
            <Menu
              id="dropOffMenuActions"
              anchorEl={anchorModal}
              keepMounted
              open={openModal}
              onClose={() => setAnchorModal(null)}
              className={classes.menu}
            >
              {getMenuItems().map(item => (
                <div key={item}>
                  <MenuItem
                    className={classes.menuItem}
                    key={item}
                    onClick={() => {
                      switch (item) {
                        case "View":
                          onView(templateDetails.id, templateDetails.mall.id);
                          handleClose();
                          break;
                        case "Duplicate":
                          onDuplicate(templateDetails.id);
                          handleClose();
                          break;
                        case isDraft ? "Continue Setup" : "Edit":
                          onEdit(templateDetails.id);
                          handleClose();
                          break;
                        case "Delete":
                          onDelete(templateDetails.id);
                          handleClose();
                          break;
                        case "Rename":
                          onRename(templateDetails.id, templateDetails.name);
                          handleClose();
                          break;
                        default:
                          handleClose();
                          break;
                      }
                    }}
                  >
                    {item}
                  </MenuItem>
                </div>
              ))}
            </Menu>
          </Popover>
        </div>
      </div>
    </div>
  );
};

export default DropOffTemplateListCard;
